import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Radio,
  Stack,
  styled,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { COLORS } from "../../../constants/colors";
import { Confirm } from "../../trendPage/customGraph/modal/confirm";
import { useTranslation } from "react-i18next";
import localizationKeys from "../../../i18n/localizationKeys";
import { deleteDashboard, postDashboard } from "../../../api/dashboard";
import { useNavigate } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import { getSortedDashboards } from "../dashboardUtil";
import { DASHBOARD_MODE } from "../constants";

const DashboardSettingsTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#A0AAB4",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": {
      borderColor: "#2c3a48",
    },
    "&:hover fieldset": {
      borderColor: "#B2BAC2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6F7E8C",
    },
  },
  "& .MuiFormLabel-root": {
    color: "rgba(255, 255, 255, 0.6)",
  },
});

export const DashboardSettingsPage = (props) => {
  const { currentVessel, setMode, setDashboards, selectedDashboard, setSelectedDashboard } = props;
  const userData = useContext(UserContext).user;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(selectedDashboard.name);
  const [description, setDescription] = useState(selectedDashboard?.description ?? "");
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [promptContent, setPromptContent] = useState({});
  const [isPublic, setIsPublic] = useState(selectedDashboard.shareLevel === 1);
  const [isEditableByOthers, setIsEditableByOthers] = useState(
    selectedDashboard.isEditableByOthers
  );
  const handleRadioChange = (e) => {
    if (e.target.value === "true") {
      setIsPublic(true);
    } else {
      setIsPublic(false);
    }
  };

  const handleToggleSwitch = (e) => {
    setIsEditableByOthers(e.target.checked);
  };

  const saveOKHandler = async (id) => {
    await postDashboard(currentVessel.id, {
      id,
      vesselId: currentVessel.id,
      ownerId: userData.id,
      name: title,
      description,
      shareLevel: isPublic ? 1 : 0,
      isEditableByOthers: isPublic && isEditableByOthers ? 1 : 0,
      items: selectedDashboard.items,
    });
    const sortedDashboards = await getSortedDashboards(currentVessel.id);
    // update selected dashboard info
    setDashboards(sortedDashboards);
    setSelectedDashboard(sortedDashboards.find((dashboard) => dashboard.id === id));
    setIsSaveModalOpen(false);
  };

  const saveDashboardHandler = (id) => {
    setIsSaveModalOpen(true);
    setPromptContent({
      title: t(localizationKeys.DashboardSettingsSaveSettingsModalTitle),
      message: t(localizationKeys.DashboardSettingsSaveSettingsModalText),
      cancelText: localizationKeys.CancelButtonText,
      okText: localizationKeys.CustomPromptSaveOkButton,
      okHandler: () => saveOKHandler(id),
      cancelHandler: () => setIsSaveModalOpen(false),
      isDelete: false,
    });
  };

  const deleteOKHandler = async (id) => {
    await deleteDashboard(id);
    const sortedDashboards = await getSortedDashboards(currentVessel.id);
    if (sortedDashboards.length > 0) {
      setMode(DASHBOARD_MODE.DISPLAY);
      setSelectedDashboard(sortedDashboards[0]);
      setDashboards(sortedDashboards);
      navigate(`/dashboard/${sortedDashboards[0].id}/${currentVessel.id}`);
    } else {
      setMode(DASHBOARD_MODE.CREATE);
      setSelectedDashboard([]);
      setDashboards([]);
      navigate(`/dashboard/${currentVessel.id}`);
    }
  };

  const deleteDashboardHandler = (id) => {
    setIsDeleteModalOpen(true);
    setPromptContent({
      title: t(localizationKeys.DashboardSettingsDeleteSettingsModalText),
      message: t(localizationKeys.DashboardSettingsDeleteSettingsModalText),
      cancelText: localizationKeys.CancelButtonText,
      okText: localizationKeys.CustomPromptDeleteOkButton,
      okHandler: () => deleteOKHandler(id),
      cancelHandler: () => setIsDeleteModalOpen(false),
      isDelete: true,
    });
  };

  return (
    <Container>
      {/* save confirmation modal */}
      <Confirm
        when={isSaveModalOpen}
        title={t(promptContent.title)}
        message={t(promptContent.message)}
        cancelText={t(promptContent.cancelText)}
        okText={t(promptContent.okText)}
        onOK={promptContent.okHandler}
        onCancel={promptContent.cancelHandler}
        isDelete={promptContent.isDelete}
      />
      {/* delete confirmation modal */}
      <Confirm
        when={isDeleteModalOpen}
        title={t(promptContent.title)}
        message={t(promptContent.message)}
        cancelText={t(promptContent.cancelText)}
        okText={t(promptContent.okText)}
        onOK={promptContent.okHandler}
        onCancel={promptContent.cancelHandler}
        isDelete={promptContent.isDelete}
      />
      <Stack spacing={6}>
        <Box
          component={"div"}
          sx={{ color: "white", cursor: "pointer", height: "50px" }}
          onClick={() => setMode(DASHBOARD_MODE.EDIT)}
        >
          <Box component={"span"}>{"<-"}</Box>
          <Box component={"span"} sx={{ marginLeft: "12px", fontWeight: 500, fontSize: "16px" }}>
            {t(localizationKeys.DashboardBackToDashboard)}
          </Box>
        </Box>
        <Stack spacing={6}>
          <Typography variant="h4" sx={{ color: "white" }}>
            {t(localizationKeys.DashboardSettingsTitle)}
          </Typography>
          <Stack sx={{ width: "40%" }} spacing={2}>
            <DashboardSettingsTextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              label={t(localizationKeys.DashboardNameFormText)}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 32 } }}
              sx={{ color: "white" }}
            ></DashboardSettingsTextField>
            <DashboardSettingsTextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              label={t(localizationKeys.DashboardDescriptionFormText)}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 32 } }}
              sx={{ color: "white" }}
            ></DashboardSettingsTextField>
          </Stack>
          <Stack direction={"row"} spacing={6}>
            <Stack spacing={2}>
              <Box component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                <Radio
                  sx={{ color: "#ffffff4c", "&.Mui-checked": { color: COLORS.greenishBlue } }}
                  value={false}
                  checked={!isPublic}
                  onChange={handleRadioChange}
                />
                <Typography sx={{ color: "white" }}>
                  {t(localizationKeys.DashboardSettingsPrivate)}
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255, 0.6)" }}>
                {t(localizationKeys.DashboardSettingsPrivateSubText)}
              </Typography>
            </Stack>
            <Stack spacing={2} style={{ color: "white" }}>
              <Box component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                <Radio
                  sx={{ color: "#ffffff4c", "&.Mui-checked": { color: COLORS.greenishBlue } }}
                  value={true}
                  checked={isPublic}
                  onChange={handleRadioChange}
                />
                <Typography sx={{ color: "white" }}>
                  {t(localizationKeys.DashboardSettingsPublic)}
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255, 0.6)" }}>
                {t(localizationKeys.DashboardSettingsPublicSubText)}
              </Typography>
              {isPublic && (
                <Box>
                  <FormControlLabel
                    control={
                      // rectangle toggle switch
                      <Switch
                        checked={isEditableByOthers}
                        onChange={handleToggleSwitch}
                        sx={{
                          ".MuiSwitch-switchBase.Mui-checked": {
                            color: "white",
                            "+ .MuiSwitch-track": { backgroundColor: COLORS.greenishBlue },
                          },
                        }}
                        slotProps={{
                          track: {
                            sx: { backgroundColor: "rgba(255, 255, 255, 0.3)", borderRadius: 0 },
                          },
                          thumb: { sx: { borderRadius: 0, width: "12px", height: "12px" } },
                          switchBase: { sx: { padding: "13px" } },
                        }}
                      />
                    }
                    label={t(localizationKeys.DashboardSettingsEditableByOthers)}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Box component={"div"} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              deleteDashboardHandler(selectedDashboard.id);
            }}
            sx={{ color: "red" }}
          >
            {t(localizationKeys.DashboardSettingsDeleteDashboardButtonText)}
          </Button>
          <Button
            onClick={() => {
              saveDashboardHandler(selectedDashboard.id);
            }}
            variant="contained"
            sx={{ color: "black", background: COLORS.greenishBlue }}
          >
            {t(localizationKeys.SaveChanges)}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

DashboardSettingsPage.propTypes = {
  currentVessel: PropTypes.object,
  setMode: PropTypes.func,
  setDashboards: PropTypes.array,
  selectedDashboard: PropTypes.object,
  setSelectedDashboard: PropTypes.func,
};
